import React, { useCallback, useEffect, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { API_BASE_URL, authFetchJson } from '../apiClient.ts';

interface Project {
  id: number;
  name: string;
}

interface Props {
  value: number | null;
  onChange: (id: number | null) => void;
  label?: string;
}

export const ProjectAutocomplete: React.FC<Props> = ({
  value,
  onChange,
  label = 'Project',
}) => {
  const [options, setOptions] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);

  const loadDefaultList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetchJson(
        `${API_BASE_URL}/projects?sort=["name","ASC"]&range=[0,49]`,
      );
      setOptions(res.json);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSelectedProject = useCallback(async () => {
    if (!value) return;
    try {
      const res = await authFetchJson(`${API_BASE_URL}/projects/${value}`);
      setOptions([res.json]);
    } catch {
      // ignore
    }
  }, [value]);

  useEffect(() => {
    if (value) loadSelectedProject();
    else loadDefaultList();
  }, [loadDefaultList, loadSelectedProject, value]);

  const handleSearch = async (input: string) => {
    if (!input) return loadDefaultList();
    setLoading(true);
    try {
      const res = await authFetchJson(
        `${API_BASE_URL}/projects?filter={"q":"${input}"}`,
      );
      setOptions(res.json);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Autocomplete
      loading={loading}
      options={options}
      value={options.find((o) => o.id === value) || null}
      getOptionLabel={(r) => r.name ?? ''}
      onInputChange={(_, text) => handleSearch(text)}
      onChange={(_, newVal) => onChange(newVal?.id ?? null)}
      renderInput={(params) => (
        <TextField {...params} label={label} fullWidth />
      )}
    />
  );
};
